import {
  orderIdentificationSchema,
  type OrderIdentificationFormValues,
} from "@babackapp/return-widget-components/forms/order-identification.form";
import { useSearchOrder } from "@babackapp/return-widget-components/mutations/use-search-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { useAuth } from "@babackapp/auth-client/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";

export type OrderIdentificationMode = "normal" | "retractation";

export function useOrderIdentificationForm() {
  const navigate = useNavigate();
  const { shopId } = useParams<{ shopId: string }>();
  const [searchParams] = useSearchParams();
  const bypassOrderId = searchParams.get("orderId");
  const { data: shop } = usePublicShop(shopId!);
  const mutation = useSearchOrder();
  const { user } = useAuth();
  const isMerchant = !!user;
  const [mode, setMode] = useState<OrderIdentificationMode>("normal");
  const hasAttemptedBypass = useRef(false);

  const validationInfo = shop.preferences?.userPortal?.validationInformations ?? {
    email: true,
    trackingNumber: false,
    zipCode: false,
  };

  const schema = isMerchant
    ? orderIdentificationSchema
    : orderIdentificationSchema.extend({
        secondaryInput: z.string().min(1, "Ce champ est requis"),
      });

  const form = useForm<OrderIdentificationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { orderName: "", secondaryInput: "" },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const data = await mutation.mutateAsync({
      orderName: values.orderName,
      secondaryInput: isMerchant ? undefined : values.secondaryInput,
      providerId: shop.providerId,
    });
    const base = `order/${data.orderId}`;
    navigate(mode === "retractation" ? `${base}/retractation-article-selection` : `${base}/return-requests`);
  });

  // ?orderId= bypass — skip the manual lookup form entirely, mirroring the old widget's
  // behavior of auto-navigating once the order resolves from the query param.
  useEffect(() => {
    if (!bypassOrderId || hasAttemptedBypass.current) return;
    hasAttemptedBypass.current = true;
    mutation
      .mutateAsync({ orderId: bypassOrderId, providerId: shop.providerId })
      .then((data) => {
        navigate(`order/${data.orderId}/return-requests`);
      })
      .catch(() => {
        // Fall through to the manual identification form on failure.
      });
  }, [bypassOrderId, shop.providerId]);

  return {
    form,
    mutation,
    onSubmit,
    logoUrl: shop.logoUrl ?? null,
    isMerchant,
    shop,
    mode,
    setMode,
    validationInfo,
    isBypassing: !!bypassOrderId && mutation.isPending,
  };
}
